import { fromMinorUnits } from '@/api/map/agent'
import { fromMinorUnitsSigned } from '@/api/map/pnl'
import type { OverviewSnapshot } from '@/data/api'
import { unstopped, type Account, type DailyPoint, type Role } from '@/data/types'
import { STOP_COPY } from '@/lib/status'
import { isoDay } from '@/lib/format'

const DAY_MS = 86_400_000

/** Same-length window immediately before `from` (matches fixture getOverview). */
export function previousRange(from: string, to: string): { from: string; to: string } {
  const fromMs = Date.parse(`${from.slice(0, 10)}T00:00:00`)
  const toMs = Date.parse(`${to.slice(0, 10)}T00:00:00`)
  const spanDays = Math.max(1, Math.round((toMs - fromMs) / DAY_MS) + 1)
  const priorEnd = fromMs - DAY_MS
  const priorStart = priorEnd - (spanDays - 1) * DAY_MS
  return { from: isoDay(priorStart), to: isoDay(priorEnd) }
}

/** analytics-service Money — minor units as string. */
export type MoneyMinor = string

export interface AnalyticsTurnoverRow {
  key: string
  label?: string
  betsPlaced?: number
  turnoverMinor: MoneyMinor
  bookNetMinor?: MoneyMinor
  members?: number
}

export interface AnalyticsTurnoverResponse {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  groupBy: 'agent' | 'market'
  rows: AnalyticsTurnoverRow[]
}

export interface AnalyticsDailyRow {
  date: string
  betsPlaced?: number
  turnoverMinor: MoneyMinor
  betsSettled?: number
  bookNetMinor: MoneyMinor
  activeMembers?: number
  bookMargin?: number | null
}

export interface AnalyticsDailyResponse {
  asOf?: string
  martBuiltAt?: string | null
  martStale?: boolean
  rows: AnalyticsDailyRow[]
}

/** One `groupBy=market` row — label often absent (no catalogue); UI falls back to key. */
export type MarketTurnoverRow = {
  key: string
  label: string
  turnover: number
  netPnl: number
  bets: number
}

/**
 * Console view of GET /turnover (agent + market) + /daily (+ member-pnl top).
 * Sport/product/competition and live unmatched stake are not in the contract —
 * flags keep the UI honest instead of inventing numbers.
 */
export type TurnoverReport = {
  asOf?: string
  martStale?: boolean
  account: Account | null
  daily: DailyPoint[]
  previous: DailyPoint[]
  bySport: OverviewSnapshot['bySport']
  byGame: OverviewSnapshot['byGame']
  /** Live `groupBy=market` rows; empty on fixture path (fixtures use byGame). */
  byMarket: MarketTurnoverRow[]
  topAgents: OverviewSnapshot['topAgents']
  topMembers: OverviewSnapshot['topMembers']
  openBets: number | null
  unmatchedStake: number | null
  activeMembers: number
  totalMembers: number | null
  /** True when live — no sport/product/competition dimension. */
  sportUnavailable: boolean
  /** True when open unmatched stake is not served by the contract. */
  livePositionUnavailable: boolean
}

function stubAccount(
  id: string,
  name: string,
  role: Role = 'master',
  parentId?: string | null,
  memberCount = 0,
): Account {
  return {
    id,
    username: name || id.slice(0, 8),
    displayName: name || id.slice(0, 8),
    role,
    parentId: parentId ?? null,
    path: parentId ? [parentId] : [],
    status: 'active',
    createdAt: '',
    lastActiveAt: null,
    creditLimit: 0,
    balance: 0,
    exposure: 0,
    settlement: 0,
    share: { sportsbook: 0, casino: 0 },
    commission: { fancy: 0, bookmaker: 0, matchOdds: 0 },
    betLimits: { min: 0, max: 0 },
    games: {
      casino: true,
      cardGame: true,
      liveGame: true,
      sportsbook: true,
      premiumSportsbook: true,
    },
    childCount: 0,
    memberCount,
    flags: [],
    // An audit/reporting stub, not a hierarchy row: nothing here can be stopped,
    // and no backend behind it could enforce a control if one were offered.
    ...unstopped('active', STOP_COPY.notWiredLive),
  }
}

export function toDailyPoint(row: AnalyticsDailyRow): DailyPoint {
  const net = fromMinorUnitsSigned(row.bookNetMinor)
  return {
    date: row.date,
    turnover: fromMinorUnits(row.turnoverMinor),
    grossPnl: net,
    commission: 0,
    netPnl: net,
    bets: row.betsSettled ?? row.betsPlaced ?? 0,
    activeMembers: row.activeMembers ?? 0,
    newMembers: 0,
    creditIn: 0,
    creditOut: 0,
  }
}

export function toAgentTurnoverRow(
  row: AnalyticsTurnoverRow,
  rootId?: string,
): OverviewSnapshot['topAgents'][number] | null {
  if (rootId && row.key === rootId) return null
  const name = row.label?.trim() || row.key.slice(0, 8)
  const members = row.members ?? 0
  return {
    account: stubAccount(row.key, name, 'master', null, members),
    turnover: fromMinorUnits(row.turnoverMinor),
    netPnl: fromMinorUnitsSigned(row.bookNetMinor),
    members,
  }
}

export function toMarketTurnoverRow(row: AnalyticsTurnoverRow): MarketTurnoverRow {
  // OpenAPI: label absent for market (no catalogue) — do not echo key as a "name".
  const label = row.label?.trim() || row.key
  return {
    key: row.key,
    label,
    turnover: fromMinorUnits(row.turnoverMinor),
    netPnl: fromMinorUnitsSigned(row.bookNetMinor),
    bets: row.betsPlaced ?? 0,
  }
}

export function toMemberTurnoverRow(row: {
  memberId: string
  name: string
  parentId?: string
  turnoverMinor: MoneyMinor
  bookNetMinor: MoneyMinor
  betsSettled?: number
  betsPlaced?: number
}): OverviewSnapshot['topMembers'][number] {
  return {
    account: stubAccount(row.memberId, row.name, 'player', row.parentId ?? null),
    turnover: fromMinorUnits(row.turnoverMinor),
    netPnl: fromMinorUnitsSigned(row.bookNetMinor),
    bets: row.betsSettled ?? row.betsPlaced ?? 0,
  }
}

/** Fixture OverviewSnapshot → TurnoverReport (sport/game/unmatched available). */
export function fromOverview(snapshot: OverviewSnapshot, asOf?: string): TurnoverReport {
  return {
    asOf: asOf ?? new Date().toISOString(),
    martStale: false,
    account: snapshot.account,
    daily: snapshot.daily,
    previous: snapshot.previous,
    bySport: snapshot.bySport,
    byGame: snapshot.byGame,
    byMarket: [],
    topAgents: snapshot.topAgents,
    topMembers: snapshot.topMembers,
    openBets: snapshot.openBets,
    unmatchedStake: snapshot.unmatchedStake,
    activeMembers: snapshot.activeMembers,
    totalMembers: snapshot.totalMembers,
    sportUnavailable: false,
    livePositionUnavailable: false,
  }
}

export function toTurnoverReport(input: {
  daily: AnalyticsDailyResponse
  previous: AnalyticsDailyResponse
  agents: AnalyticsTurnoverResponse
  markets: AnalyticsTurnoverResponse
  members: Array<{
    memberId: string
    name: string
    parentId?: string
    turnoverMinor: MoneyMinor
    bookNetMinor: MoneyMinor
    betsSettled?: number
    betsPlaced?: number
  }>
  rootId?: string
}): TurnoverReport {
  const daily = (input.daily.rows ?? []).map(toDailyPoint)
  const previous = (input.previous.rows ?? []).map(toDailyPoint)
  const topAgents = (input.agents.rows ?? [])
    .map((r) => toAgentTurnoverRow(r, input.rootId))
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .sort((a, b) => b.turnover - a.turnover)
  const byMarket = (input.markets.rows ?? [])
    .map(toMarketTurnoverRow)
    .filter((r) => r.turnover > 0)
    .sort((a, b) => b.turnover - a.turnover)
  const topMembers = input.members
    .map(toMemberTurnoverRow)
    .filter((m) => m.turnover > 0)
    .sort((a, b) => b.turnover - a.turnover)
    .slice(0, 10)

  const activeMembers = Math.max(0, ...daily.map((d) => d.activeMembers), 0)

  return {
    asOf:
      input.daily.asOf ??
      input.agents.asOf ??
      input.markets.asOf ??
      input.previous.asOf,
    martStale: Boolean(
      input.daily.martStale || input.agents.martStale || input.markets.martStale,
    ),
    account: null,
    daily,
    previous,
    bySport: [],
    byGame: [],
    byMarket,
    topAgents,
    topMembers,
    openBets: null,
    unmatchedStake: null,
    activeMembers,
    totalMembers: null,
    sportUnavailable: true,
    livePositionUnavailable: true,
  }
}

/** Soft-fail shell when live turnover endpoints are unreachable. */
export function emptyTurnoverReport(): TurnoverReport {
  return {
    account: null,
    daily: [],
    previous: [],
    bySport: [],
    byGame: [],
    byMarket: [],
    topAgents: [],
    topMembers: [],
    openBets: null,
    unmatchedStake: null,
    activeMembers: 0,
    totalMembers: null,
    sportUnavailable: true,
    livePositionUnavailable: true,
  }
}
